import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const SelectableCard = ({
    label,
    selected,
    onClick,
    icon,
    disabled = false,
    customStyle = null,
    className = '',
    layout = 'inline'   // 'inline' | 'icon-top'
}) => {
    const { getTenantSetting } = useAuth();
    const [themeColors, setThemeColors] = useState({});
    const [currentBgColor, setCurrentBgColor] = useState('#ffffff');

    useEffect(() => {
        const loadThemeColors = async () => {
            if (getTenantSetting) {
                const colors = {
                    primary: await getTenantSetting('theme.primary_color', '#0ea5e9'),
                    selected: await getTenantSetting('theme.card_selected_color', '#dbeafe'),
                    hover: await getTenantSetting('theme.card_hover_color', '#f3f4f6'),
                    normal: await getTenantSetting('theme.card_normal_color', '#ffffff'),
                    text: await getTenantSetting('theme.text_color', '#0c4a6e')
                };
                setThemeColors(colors);
                // Set initial background color
                setCurrentBgColor(selected ? colors.selected : colors.normal);
            }
        };
        loadThemeColors();
    }, [getTenantSetting, selected]);

    // Update background color when selection changes
    useEffect(() => {
        if (themeColors.normal) {
            setCurrentBgColor(selected ? themeColors.selected : themeColors.normal);
        }
    }, [selected, themeColors]);

    // If custom style is provided, use it entirely
    if (customStyle) {
        return (
            <div
                onClick={disabled ? null : onClick}
                className={`cursor-pointer p-3 rounded-lg border transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
                style={customStyle}
            >
                <div className="flex items-center justify-center">
                    {icon && <span className="mr-2">{icon}</span>}
                    <span className="text-sm font-medium">{label}</span>
                </div>
            </div>
        );
    }

    // Normal card handling
    const borderColor = selected ? themeColors.primary : '#d1d5db';
    const textColor = selected ? themeColors.text : '#374151';

    const hoverHandlers = {
        onMouseEnter: () => {
            if (!selected && !disabled && themeColors.hover) setCurrentBgColor(themeColors.hover);
        },
        onMouseLeave: () => {
            if (!selected && !disabled && themeColors.normal) setCurrentBgColor(themeColors.normal);
        },
    };

    if (layout === 'icon-top') {
        return (
            <div
                onClick={disabled ? null : onClick}
                className={`cursor-pointer rounded-lg border transition-all flex flex-col items-center justify-center py-4 px-2 gap-2 min-h-[90px] ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
                style={{
                    backgroundColor: currentBgColor,
                    borderColor: borderColor,
                    borderBottomWidth: selected ? '3px' : '1px',
                    borderBottomColor: selected ? themeColors.primary : '#d1d5db',
                    color: textColor,
                }}
                {...hoverHandlers}
            >
                {icon && <span style={{ backgroundColor: 'transparent' }}>{icon}</span>}
                <span className="text-xs font-semibold text-center uppercase tracking-wide leading-tight" style={{ backgroundColor: 'transparent' }}>{label}</span>
            </div>
        );
    }

    return (
        <div
            onClick={disabled ? null : onClick}
            className={`cursor-pointer p-3 rounded-lg border transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            style={{
                backgroundColor: currentBgColor,
                borderColor: borderColor,
                color: textColor
            }}
            {...hoverHandlers}
        >
            <div
                className="flex items-center justify-center"
                style={{ backgroundColor: 'transparent' }}
            >
                {icon && <span className="mr-2" style={{ backgroundColor: 'transparent' }}>{icon}</span>}
                <span className="text-sm font-medium" style={{ backgroundColor: 'transparent' }}>{label}</span>
            </div>
        </div>
    );
};

export default SelectableCard;